/**
 * =========================================================================================
 *     Copyright © 2026 Dobler Data Solutions, LLC. ALL RIGHTS RESERVED.
 * =========================================================================================
 *     File            :   CostSourceBadge.tsx
 *     Feature         :   259-real-billing-cost-consistency (US5 / FR-007)
 *     Description     :   The ONE canonical cost-source label — every cost $ in the app is
 *                         accompanied by <CostSourceBadge source="actual"|"modeled" />, so a
 *                         viewer can tell real Azure billing from a modeled estimate at a
 *                         glance. Built on the kit Badge + AppTooltip + tokens (no new styling
 *                         system). Also exports the shared formatUsd() + costWindowLabel() so
 *                         "/ mo = trailing 30 days" and USD formatting live in one place.
 *     Author          :   Peter Dobler
 *     Created         :   2026-07-27
 * =========================================================================================
 */

import React from 'react';
import { CircleDollarSign, Calculator } from 'lucide-react';
import { Badge } from './base/Badge';
import { AppTooltip } from './AppTooltip';

export const COST_SOURCE_ACTUAL = 'Actual Azure billing';
export const COST_SOURCE_MODELED = 'Modeled estimate';
/** Spec 298 FR-008: billed spend keeps the resources retired since (owner, Session 2026-11-03), and the screen says so. */
export const COST_INCLUDES_RETIRED = 'includes retired resources';

const TOOLTIP_ACTUAL =
  'Real Azure billing from dbo.azure_cost_daily (Cost Management), summed over the window. Not an estimate. ' +
  'It includes what resources retired since then were billed, because that spend was incurred.';
const TOOLTIP_MODELED =
  'A modeled estimate (SKU / list-price rate) — NOT actual billing. Kept only where Azure cannot attribute a real figure (e.g. a right-sizing counterfactual or per-client OneLake).';

/** Shared USD formatter — one place for every cost $ (replaces the per-file local `usd()`). */
export const formatUsd = (v: number | null | undefined): string =>
  v == null ? '—' : `$${v.toLocaleString(undefined, { maximumFractionDigits: 2 })}`;

/** The canonical "/ mo" window label (Spec 259: "/ mo" = trailing-30-day sum). */
export const costWindowLabel = (): string => 'Last 30 days';

export interface CostSourceBadgeProps {
  source: 'actual' | 'modeled';
  /** Optional window suffix, e.g. "Last 30 days". */
  window?: string;
}

export const CostSourceBadge: React.FC<CostSourceBadgeProps> = ({ source, window }) => {
  const actual = source === 'actual';
  const Icon = actual ? CircleDollarSign : Calculator;
  return (
    <AppTooltip title={actual ? TOOLTIP_ACTUAL : TOOLTIP_MODELED}>
      <Badge variant={actual ? 'success' : 'secondary'} size="xs">
        <Icon className="mr-1 inline h-3 w-3" aria-hidden />
        {actual ? COST_SOURCE_ACTUAL : COST_SOURCE_MODELED}
        {window ? ` · ${window}` : ''}
        {actual ? ` · ${COST_INCLUDES_RETIRED}` : ''}
      </Badge>
    </AppTooltip>
  );
};

export default CostSourceBadge;
